import { Avatar, formatName } from '@/components/Avatar';
import LinkButton from '@/components/LinkButton';
import { columnOverrides } from '@/components/table';
import { UserInfo } from '@/lib/models';
import { TableContainer } from '@/lib/styled';
import {
  getGroupAdmins,
  getOrgAdmins,
  getSpaceAdmins,
  getSpaceUsers,
  useQueryListUsers,
} from '@/pages/admin/user/hook';
import { GroupIcon, SpaceIcon } from '@/pages/home/icons';
import { WorkGroupT, WorkspaceT } from '@/pages/workgroup/type';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Spinner } from '@tigergraph/app-ui-lib/spinner';
import { TableBuilder } from '@tigergraph/app-ui-lib/table';
import { TableBuilderColumn } from 'baseui/table-semantic';
import { useMemo, useState } from 'react';
import GroupAdminDetail from './GroupAdminDetail';
import { calculateRoleForGroup, calculateRoleForSpace } from '@/pages/admin/user/type';
import { useOrgContext } from '@/contexts/orgContext';
import SpaceUserManagement from '@/pages/workgroup/tab/iam/SpaceUserManagement';

type TableData =
  | {
      type: 'group';
      group: WorkGroupT;
    }
  | {
      type: 'space';
      space: WorkspaceT;
    };

export default function Access({ group }: { group: WorkGroupT }) {
  const [css] = useStyletron();
  const { data: allUser = [], isLoading: isAllUserLoading } = useQueryListUsers();

  const [showGroupAdminDetail, setShowGroupAdminDetail] = useState(false);
  const [showSpaceAdminDetail, setShowSpaceAdminDetail] = useState(false);
  const [space, setSpace] = useState<WorkspaceT | undefined>(undefined);

  const data = useMemo(() => {
    const data: TableData[] = [
      {
        type: 'group',
        group: group,
      },
      ...group.workspaces.map((s) => ({
        type: 'space' as const,
        space: s,
      })),
    ];
    return data;
  }, [group]);

  const { userInfo } = useOrgContext();
  const isGroupAdmin = calculateRoleForGroup(userInfo.roles, group.workgroup_id) === 'workgroup-admins';

  return (
    <TableContainer>
      <TableBuilder data={data}>
        <TableBuilderColumn header="Workgroup" id="workgroup" overrides={columnOverrides}>
          {(row: TableData) => {
            if (row.type === 'group') {
              return (
                <LinkButton disabled={!isGroupAdmin} onClick={() => setShowGroupAdminDetail(true)}>
                  <GroupIcon /> Workgroup
                </LinkButton>
              );
            }
            return (
              <LinkButton
                onClick={() => {
                  setShowSpaceAdminDetail(true);
                  setSpace(row.space);
                }}
                $style={{ marginLeft: '20px' }}
                disabled={
                  calculateRoleForSpace(userInfo.roles, row.space.workgroup_id, row.space.workspace_id) !==
                  'workspace-admins'
                }
              >
                <SpaceIcon /> {row.space.name}
              </LinkButton>
            );
          }}
        </TableBuilderColumn>
        <TableBuilderColumn header="Creator" id="creator">
          {(row: TableData) => {
            if (row.type === 'group') {
              return row.group.creator;
            }
            return row.space.creator;
          }}
        </TableBuilderColumn>
        <TableBuilderColumn
          header="User"
          id="user"
          overrides={{
            TableBodyCell: {
              style: {
                paddingTop: '8px',
                paddingBottom: '8px',
                verticalAlign: 'center',
              },
            },
          }}
        >
          {(row: TableData) => {
            if (isAllUserLoading) {
              return <Spinner $size={'16px'} $borderWidth={'2px'} />;
            }
            let users: UserInfo[] = [];
            if (row.type === 'group') {
              users = getGroupAdmins(allUser, group.workgroup_id).concat(getOrgAdmins(allUser));
            } else {
              users = getSpaceAdmins(allUser, group.workgroup_id, row.space.workspace_id).concat(
                getSpaceUsers(allUser, group.workgroup_id, row.space.workspace_id)
              );
            }
            return (
              <div className={css({ display: 'flex' })}>
                {users.slice(0, 15).map((user, index) => (
                  <div
                    key={user.id}
                    className={css({
                      padding: '2px',
                      borderRadius: '50%',
                      position: 'relative',
                      left: `-${index * 6}px`,
                      backgroundColor: '#fff',
                      display: 'flex',
                      overflow: 'hidden',
                    })}
                  >
                    <Avatar size="compact">{formatName(user)}</Avatar>
                  </div>
                ))}
              </div>
            );
          }}
        </TableBuilderColumn>
      </TableBuilder>
      {showGroupAdminDetail ? (
        <GroupAdminDetail
          group={group}
          isOpen={showGroupAdminDetail}
          onClose={() => {
            setShowGroupAdminDetail(false);
            setSpace(undefined);
          }}
        />
      ) : null}
      {showSpaceAdminDetail && space ? (
        <SpaceUserManagement
          space={space}
          group={group}
          isOpen={showSpaceAdminDetail}
          onClose={() => setShowSpaceAdminDetail(false)}
        />
      ) : null}
    </TableContainer>
  );
}
